import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  TextField,
  CircularProgress,
  Stack,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import logger from '../utils/logger';
import { getAuthHeaders } from '../utils/api';

// File type configurations
const ALLOWED_TYPES = {
  'audio/mp4': '.m4a',              // MPEG-4 Audio
  'audio/x-m4a': '.m4a',            // Apple MPEG-4 Audio
  'video/mp4': '.mp4',              // MP4 Video
  'video/webm': '.webm'             // WebM Video
};

const MAX_FILE_SIZE = 300 * 1024 * 1024; // 300MB in bytes
const CHUNK_SIZE = 4.5 * 1024 * 1024;    // 4.5MB chunk size

const UPLOAD_STEPS = ['Preparing Upload', 'Uploading to Server', 'Processing File'];

class ChunkedUploader {
  constructor(file, onProgress, onError, onSuccess, metadata = {}) {
    this.file = file;
    this.onProgress = onProgress;
    this.onError = onError;
    this.onSuccess = onSuccess;
    this.metadata = metadata;
    this.chunkSize = CHUNK_SIZE;
    this.totalChunks = Math.ceil(file.size / this.chunkSize);
    this.currentChunk = 0;
    this.fileId = Date.now().toString();
    this.aborted = false;

    logger.info('Initializing chunked upload', {
      component: 'ManualUpload',
      fileId: this.fileId,
      fileName: file.name,
      fileSize: file.size,
      totalChunks: this.totalChunks,
      chunkSize: this.chunkSize
    });
  }

  async start() {
    try {
      while (this.currentChunk < this.totalChunks && !this.aborted) {
        const start = this.currentChunk * this.chunkSize;
        const end = Math.min(start + this.chunkSize, this.file.size);
        const chunk = this.file.slice(start, end);

        await this.uploadChunk(chunk);
        this.currentChunk++;
        
        // Calculate and report progress
        const progress = (this.currentChunk / this.totalChunks) * 100;
        this.onProgress(progress);

        logger.debug('Chunk upload progress', {
          component: 'ManualUpload',
          fileId: this.fileId,
          chunkNumber: this.currentChunk,
          progress: Math.round(progress)
        });
      }

      if (!this.aborted) {
        logger.info('File upload completed', {
          component: 'ManualUpload',
          fileId: this.fileId,
          fileName: this.file.name,
          totalChunks: this.totalChunks
        });
        this.onSuccess();
      }
    } catch (error) {
      logger.error('File upload failed', {
        component: 'ManualUpload',
        fileId: this.fileId,
        error: error.message,
        currentChunk: this.currentChunk
      });
      this.onError(error.message);
    }
  }

  async uploadChunk(chunk) {
    logger.debug('Uploading chunk', {
      component: 'ManualUpload',
      fileId: this.fileId,
      chunkNumber: this.currentChunk,
      chunkSize: chunk.size
    });

    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/upload/chunk`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/octet-stream',
        'uploader-chunk-number': this.currentChunk.toString(),
        'uploader-chunks-total': this.totalChunks.toString(),
        'uploader-file-id': this.fileId,
        'uploader-file-name': this.file.name,
        'uploader-file-size': this.file.size.toString(),
        'content-length': chunk.size.toString(),
        'uploader-meeting-time': this.metadata.meetingTime || '',
        'uploader-uploaded-by': this.metadata.uploadedBy || ''
      },
      body: chunk
    });

    const responseData = await response.text();
    
    if (!response.ok) {
      try {
        const error = JSON.parse(responseData);
        logger.error('Chunk upload failed', {
          component: 'ManualUpload',
          fileId: this.fileId,
          chunkNumber: this.currentChunk,
          error: error.error || 'Upload failed'
        });
        throw new Error(error.error || 'Upload failed');
      } catch (e) {
        throw new Error(`Upload failed: ${responseData}`);
      }
    }

    try {
      return JSON.parse(responseData);
    } catch (e) {
      throw new Error('Invalid response format from server');
    }
  }

  abort() {
    this.aborted = true;
    logger.info('Upload aborted', {
      component: 'ManualUpload',
      fileId: this.fileId,
      currentChunk: this.currentChunk,
      totalChunks: this.totalChunks
    });
  }
}

const ManualUpload = () => {
  const { currentUser } = useAuth();
  const { uploadState, updateUploadState, resetUploadState } = useUpload();
  const fileInputRef = useRef();
  const uploaderRef = useRef(null);

  // Log user info for debugging
  useEffect(() => {
    logger.debug('Current user info in ManualUpload', {
      component: 'ManualUpload',
      userEmail: currentUser?.email,
      userId: currentUser?.uid,
      hasUser: !!currentUser,
      userAttributes: currentUser ? Object.keys(currentUser) : []
    });
  }, [currentUser]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (uploaderRef.current && uploadState.uploading) {
        logger.debug('Upload component unmounting with active upload', {
          component: 'ManualUpload',
          fileId: uploadState.fileId,
          progress: uploadState.uploadProgress
        });
        uploaderRef.current = null;
      }
    };
  }, [uploadState.uploading, uploadState.fileId, uploadState.uploadProgress]);

  const validateFile = (file) => {
    logger.debug('Validating file', {
      component: 'ManualUpload',
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      hasMeetingTime: !!uploadState.meetingTime
    });

    if (!uploadState.meetingTime) {
      return 'Please select a meeting time before uploading.';
    }

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const isValidType = ALLOWED_TYPES.hasOwnProperty(file.type);
    const isValidExtension = Object.values(ALLOWED_TYPES).includes(fileExtension);

    if (!isValidType || !isValidExtension) {
      logger.warn('Invalid file type attempted', {
        component: 'ManualUpload',
        fileType: file.type,
        extension: fileExtension,
        allowedTypes: Object.keys(ALLOWED_TYPES)
      });
      return 'Invalid file type. Only MP4 video, MPEG-4/Apple MPEG-4 audio, and WebM video files are allowed.';
    }

    if (file.size > MAX_FILE_SIZE) {
      logger.warn('File size exceeds limit', {
        component: 'ManualUpload',
        fileSize: file.size,
        maxSize: MAX_FILE_SIZE
      });
      return 'File size too large. Maximum size is 300MB.';
    }

    return null;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    logger.debug('User selected file', {
      component: 'ManualUpload',
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      action: 'file_selection'
    });

    const error = validateFile(file);
    if (error) {
      logger.warn('File validation failed', {
        component: 'ManualUpload',
        error,
        fileName: file.name
      });
      updateUploadState({
        uploadError: error,
        selectedFile: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    logger.info('Starting new file upload', {
      component: 'ManualUpload',
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      meetingTime: uploadState.meetingTime
    });

    // Reset states
    updateUploadState({
      uploading: true,
      uploadProgress: 0,
      uploadError: null,
      uploadSuccess: false,
      activeStep: 0,
      uploadStatus: 'Preparing file for upload...',
      selectedFile: file,
      fileId: Date.now().toString()
    });

    // Create and start chunked upload
    const uploader = new ChunkedUploader(
      file,
      (progress) => {
        updateUploadState({
          uploadProgress: progress,
          activeStep: 1,
          uploadStatus: 'Uploading file to server...'
        });
      },
      (error) => {
        logger.error('Upload error occurred', {
          component: 'ManualUpload',
          error,
          fileName: file.name,
          fileId: uploadState.fileId
        });
        updateUploadState({
          uploadError: error,
          uploadStatus: 'Upload failed',
          uploading: false
        });
      },
      () => {
        logger.debug('Upload completed successfully', {
          component: 'ManualUpload',
          fileName: file.name,
          fileId: uploadState.fileId,
          meetingTime: uploadState.meetingTime
        });
        updateUploadState({
          uploadSuccess: true,
          uploading: false,
          activeStep: 2,
          uploadStatus: 'File uploaded successfully!'
        });
      },
      {
        meetingTime: uploadState.meetingTime,
        uploadedBy: currentUser?.email || currentUser?.['cognito:username'] || 'Unknown'
      }
    );

    uploaderRef.current = uploader;
    uploader.start();
  };

  const handleCancel = () => {
    if (uploaderRef.current) {
      uploaderRef.current.abort();
      resetUploadState();
      // Clear file input if it exists
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleMeetingTimeChange = (newValue) => {
    updateUploadState({ 
      meetingTime: newValue,
      uploadError: null 
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Upload Meeting Recording
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ mb: 3 }}>
            <DateTimePicker
              label="Meeting Time *"
              value={uploadState.meetingTime}
              onChange={handleMeetingTimeChange}
              slotProps={{ textField: { fullWidth: true, required: true } }}
              disableFuture
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: 'center' }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              accept={Object.values(ALLOWED_TYPES).join(',')}
            />
            <Button
              variant="contained"
              component="label"
              startIcon={uploadState.uploading ? <CircularProgress size={20} /> : <CloudUpload />}
              onClick={() => fileInputRef.current.click()}
              disabled={uploadState.uploading || !uploadState.meetingTime}
            >
              {uploadState.uploading ? 'Uploading...' : 'Select File to Upload'}
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Supported formats: MP4 Audio (.m4a), MP4 Video (.mp4), and WebM Video (.webm). Maximum file size: 300MB
            </Typography>
            {!uploadState.meetingTime && !uploadState.uploading && (
              <Typography color="error" sx={{ mt: 1 }}>
                Please select a meeting time before uploading
              </Typography>
            )}
          </Box>

          {uploadState.selectedFile && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Selected file: {uploadState.selectedFile.name} ({formatFileSize(uploadState.selectedFile.size)})
              </Typography>
            </Box>
          )}

          {uploadState.uploading && (
            <Box sx={{ mt: 3 }}>
              <Stepper activeStep={uploadState.activeStep} alternativeLabel>
                {UPLOAD_STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ mt: 2 }}>
                <Stack spacing={1} alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    {uploadState.uploadStatus}
                  </Typography>
                  <LinearProgress 
                    variant={uploadState.uploadProgress === 0 ? "indeterminate" : "determinate"} 
                    value={uploadState.uploadProgress} 
                    sx={{ width: '100%', height: 8, borderRadius: 4 }}
                  />
                  {uploadState.uploadProgress > 0 && (
                    <Typography variant="body2" color="textSecondary">
                      {Math.round(uploadState.uploadProgress)}% Complete
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancel}
                    sx={{ mt: 1 }}
                  >
                    Cancel Upload
                  </Button>
                </Stack>
              </Box>
            </Box>
          )}

          {uploadState.uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadState.uploadError}
            </Alert>
          )}

          {uploadState.uploadSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              File uploaded successfully!
            </Alert>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default ManualUpload;
